import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  // Iter495 — Google OAuth users land without a phone. We gate access to the
  // rest of the app until they supply one. The complete-profile page itself
  // bypasses this so users can actually reach it.
  const isCompleteProfilePath = location.pathname === '/complete-profile';
  const UK_PHONE_RE_PROT = /^07\d{9}$/;
  const needsPhone = (u) => !u?.phone || !UK_PHONE_RE_PROT.test(String(u.phone).replace(/\s/g, ''));

  useEffect(() => {
    // If user data was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      if (!isCompleteProfilePath && needsPhone(location.state.user)) {
        navigate('/complete-profile', { replace: true });
        return;
      }
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });

        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);

          // Check admin requirement
          if (requireAdmin && !['admin', 'superadmin'].includes(response.data.role)) {
            navigate('/', { replace: true });
            return;
          }

          // Phone-gate (skip for admins so they can still operate without phone)
          const isAdmin = ['admin', 'superadmin'].includes(response.data.role);
          if (!isAdmin && !isCompleteProfilePath && needsPhone(response.data)) {
            navigate('/complete-profile', { replace: true });
            return;
          }
        } else {
          throw new Error('No user data');
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login', {
          replace: true,
          state: { from: location.pathname }
        });
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.user, navigate, requireAdmin, location.pathname]);

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-900 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (isAuthenticated === false) {
    return null;
  }

  // Authenticated - render children
  return children;
};

export default ProtectedRoute;
